import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DollarSign, TrendingUp, Calendar, Award, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

const fmt = (n) => n == null ? '—' : `$${parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const CommissionDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ax.get('/api/admin/commission/me');
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load commission data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Loader2 size={28} className="animate-spin" style={{ color: 'var(--teal)' }} /></div>;
  if (error) return <div style={{ padding: '2rem', textAlign: 'center', color: '#991B1B' }}>{error}</div>;
  if (!data) return null;

  const cards = [
    { label: 'This Month', value: data.mtd, icon: <Calendar size={18} />, color: '#10B981' },
    { label: 'This Quarter', value: data.qtd, icon: <TrendingUp size={18} />, color: '#3B82F6' },
    { label: 'Year to Date', value: data.ytd, icon: <Award size={18} />, color: '#8B5CF6' },
    { label: 'All Time', value: data.all_time, icon: <DollarSign size={18} />, color: '#F59E0B' },
  ];

  return (
    <div data-testid="commission-dashboard">
      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)', margin: '0 0 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <DollarSign size={22} style={{ color: '#10B981' }} />
        My Commission
      </h2>

      {/* Earnings cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
        {cards.map((c, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '1rem' }} data-testid={`commission-card-${i}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, color: c.color }}>{c.icon}<span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>{c.label}</span></div>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', fontWeight: 700, color: 'var(--navy)' }}>{fmt(c.value)}</div>
          </div>
        ))}
      </div>

      {/* Pipeline projection */}
      <div style={{ background: 'linear-gradient(135deg, #F0FDFA 0%, #ECFDF5 100%)', border: '1px solid #A7F3D0', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#065F46', textTransform: 'uppercase' }}>Projected Pipeline Commission</div>
          <div style={{ fontSize: '0.813rem', color: '#047857', marginTop: 2 }}>Weighted by stage probability across {data.pipeline_count} open deals</div>
        </div>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.75rem', fontWeight: 700, color: '#065F46' }}>{fmt(data.projected_pipeline)}</div>
      </div>

      {/* Recent won deals */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid #F3F4F6' }}>
          <div style={{ fontWeight: 700, color: 'var(--navy)' }}>Recent Won Deals</div>
          <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>{data.won_count} total deals won</div>
        </div>
        {data.recent_won.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.875rem' }}>No closed deals yet. Get to it!</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                <th style={th}>Customer</th>
                <th style={th}>Company</th>
                <th style={{ ...th, textAlign: 'right' }}>Deal Value</th>
                <th style={{ ...th, textAlign: 'right' }}>Commission</th>
                <th style={th}>Closed</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_won.map((d) => (
                <tr key={d.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                  <td style={td}>{d.name}</td>
                  <td style={td}>{d.company || '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmt(d.deal_value)}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(d.commission_amount)}</td>
                  <td style={td}>{d.closed_at ? new Date(d.closed_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const th = { padding: '0.625rem 0.875rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' };
const td = { padding: '0.75rem 0.875rem', fontSize: '0.875rem', color: 'var(--navy)' };
